import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { toast } from "components/ui/use-toast";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { IJustification } from "src/@types/justification";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useGetAccountingAccountsActiveQuery } from "@/services/account-accouting";
import { useUpdateJustificationMutation } from "@/services/justification";
import { MESSAGES } from "../utils/messages";
import { FormSchema } from "./validate-form";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import { Separator } from "components/ui/separator";

const APPLIES_TO_OPTIONS = [
  { value: "NSU", label: "Apenas NSU" },
  { value: "DIVERGENCE", label: "Apenas Divergência" },
  { value: "BOTH", label: "Ambas" },
];

function Update({ row }: { row: IJustification }) {
  const [updateJustification, { isLoading }] = useUpdateJustificationMutation();
  const { handleModal } = useDialogModal();
  const { data: accountingAccounts } = useGetAccountingAccountsActiveQuery();

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      account_id: "",
      is_previous_day: false,
      is_next_day: false,
      applies_to: "BOTH" as "NSU" | "DIVERGENCE" | "BOTH",
      can_close_with_surplus: false,
    },
  });

  async function onSubmit(data: {
    account_id: string;
    name: string;
    is_previous_day: boolean;
    is_next_day: boolean;
    applies_to: "NSU" | "DIVERGENCE" | "BOTH";
    can_close_with_surplus: boolean;
  }) {
    const parsedAccountId = Number(data.account_id);
    try {
      await updateJustification({
        name: data?.name || "",
        version: row?.version || 0,
        account_id: parsedAccountId,
        id: Number(row?.id),
        status: row?.status || "",
        is_previous_day: data?.is_previous_day || false,
        is_next_day: data?.is_next_day || false,
        applies_to: data?.applies_to,
        can_close_with_surplus: data?.can_close_with_surplus || false,
      }).unwrap();
      toast(MESSAGES.UPDATE.SUCCESS);
      handleModal({
        isOpen: false,
      });
      form.reset({
        name: row.name,
      });
    } catch (e) {
      toast(MESSAGES.UPDATE.ERROR);
    }
  }

  useEffect(() => {
    form.reset({
      name: row?.name || "",
      account_id: String(row?.account?.id),
      is_previous_day: row?.is_previous_day || false,
      is_next_day: row?.is_next_day || false,
      applies_to: (row?.applies_to as "NSU" | "DIVERGENCE" | "BOTH") || "BOTH",
      can_close_with_surplus: row?.can_close_with_surplus || false,
    });
  }, [row, form]);

  return (
    <div className="w-xl max-w-xl bg-white rounded-lg">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 w-full"
        >
          {/* Seção: Dados Básicos */}
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="justification">Nome da Justificativa *</Label>
                  <FormControl>
                    <Input
                      {...field}
                      id="justification"
                      placeholder="Ex: Sobra de caixa, Falta de troco..."
                      className={`mt-1 ${form.formState.errors.name ? "border-red-500" : ""
                        }`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="account_id"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="account_id">Conta Contábil *</Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={String(field.value)}
                    value={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger
                        id="account_id"
                        className={`mt-1 ${form.formState.errors.account_id ? "border-red-500" : ""
                          }`}
                      >
                        <SelectValue placeholder="Selecione uma conta contábil" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accountingAccounts?.map((accountingAccount) => (
                        <SelectItem
                          key={String(accountingAccount.id)}
                          value={String(accountingAccount.id)}
                        >
                          {accountingAccount.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Seção: Referência de Data */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Referência de Data</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Selecione se esta justificativa se refere a um dia específico
              </p>
            </div>
            <div className="flex gap-6 pl-1">
              <FormField
                control={form.control}
                name="is_previous_day"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          form.setValue("is_previous_day", !!checked);
                          if (checked) form.setValue("is_next_day", false);
                        }}
                      />
                    </FormControl>
                    <Label
                      htmlFor="is_previous_day"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Dia Anterior
                    </Label>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_next_day"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          form.setValue("is_next_day", !!checked);
                          if (checked) form.setValue("is_previous_day", false);
                        }}
                      />
                    </FormControl>
                    <Label
                      htmlFor="is_next_day"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Dia Seguinte
                    </Label>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          {/* Seção: Aplicabilidade */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Aplicabilidade</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Defina onde esta justificativa pode ser utilizada
              </p>
            </div>
            <FormField
              control={form.control}
              name="applies_to"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="applies_to">Aplicável em *</Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        id="applies_to"
                        className={`mt-1 ${form.formState.errors.applies_to ? "border-red-500" : ""
                          }`}
                      >
                        <SelectValue placeholder="Selecione onde se aplica" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {APPLIES_TO_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Seção: Regras de Fechamento */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Regras de Fechamento</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Configure como esta justificativa afeta o fechamento da conciliação
              </p>
            </div>
            <FormField
              control={form.control}
              name="can_close_with_surplus"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        form.setValue("can_close_with_surplus", !!checked);
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <Label
                      htmlFor="can_close_with_surplus"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Permite fechar com falta sem lançar perda
                    </Label>
                    <FormDescription>
                      Quando marcado, permite fechar a conciliação com diferenças acima de -R$ 5,00 sem exigir lançamento de perda
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleModal({ isOpen: false })}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isLoading}
            >
              {isLoading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default Update;
